import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import {
  ChakraProvider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text
} from "@chakra-ui/react";
import "@aws-amplify/ui-react/styles.css";
import { Hub } from "aws-amplify/utils";
import {
  signIn,
  signInWithRedirect,
  signOut,
  AuthUser,
} from "aws-amplify/auth";
import Loader from "./components/loader/Loader";
import {
  // CONTACT_US_ROUTE,
  HOME_ROUTE,
  DOWNLOAD_ROUTE,
  LOGIN_ROUTE,
  SIGNUP_ROUTE,
  PORTAL_CONTROL_ROUTE,
  HELP_AND_SUPPORT_ROUTE,
  UPLOAD_PAGE_REDEFINED_ROUTE,
  DASHBOARD_ROUTE,
  UPDATE_FILES_ROUTE,
} from "./utils/constants/routes";
import LoginComponent from "./components/login/CustomLoginPage";
import SignUpPage from "./components/login/SignUpPage";
import { AuthProvider } from "./utils/hooks/authContext";
import { TableDataProvider } from "./utils/hooks/TableContext";
import { DataProvider } from "./utils/hooks/DataContext";
// import { Heading } from "@chakra-ui/react";
import { usePageAccess } from "./utils/hooks/usePageAccess";
import { useRefreshUserData } from "./utils/hooks/userContext";
import theme from './theme'
import { fetchAuthSession } from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";


const HomePage = lazy(() => import("./pages/HomePage"));
const AppLayout = lazy(() => import("./layouts/AppLayout"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const PortalControlPage = lazy(() => import("./pages/PortalControlPage"));
const HelpAndSupportPage = lazy(() => import("./pages/HelpAndSupportPage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const NewSUDashboard = lazy(() => import("./pages/SUDashboard"));
const UpdateFilesPage = lazy(() => import("./pages/UpdateFilesPage"));

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  usePageAccess();
  return element;
};

const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const refreshUserData = useRefreshUserData();
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [isAccessChecked, setIsAccessChecked] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);


const getCurrentUserCompact = async (): Promise<AuthUser> => {
  const session = await fetchAuthSession();

  if (!session.tokens?.idToken) {
    throw new Error("No session");
  }

  const payload = session.tokens.idToken.payload;

  return {
    userId: payload.sub ?? "",

    // ✅ Normalize username (same as local)
    username: payload.sub ?? "",

    signInDetails: payload.email
      ? {
          loginId: payload.email as string,

          // ✅ Only include for local users if available
          ...(payload["cognito:username"]
            ? { authFlowType: "USER_SRP_AUTH" as const }
            : {}),
        }
      : undefined,
  };
};



  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          checkAuthState();
          break;
        case "signInWithRedirect_failure":
          setError("An error occurred during authentication.");
          setAuthChecked(true);
          break;
      }
    });

    checkAuthState();

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) return; // ✅ Run ONLY when logged in

    console.log("🟢 Starting global session watchdog");

    const checkSession = async () => {
      try {
        await getCurrentUserCompact();
        console.log("✅ Session is active");
      } catch (error: any) {
        if (error?.name === "NetworkError") {
          console.warn("Temporary network issue, skipping logout");
          return;
        }
        console.log("❌ Session expired → logging out");
        await signOut();
        setUser(null);

        // ✅ Hard redirect to login (prevents back navigation)
        window.location.replace(LOGIN_ROUTE);
      }
    };

    // ✅ Run immediately once after login
    checkSession();

    // ✅ Run every 15 minutes (independent of page changes)
    const intervalId = setInterval(checkSession, 15 * 60 * 1000);

    // ✅ Cleanup when user logs out or app unmounts
    return () => {
      console.log("🔴 Stopping global session watchdog");
      clearInterval(intervalId);
    };
  }, [user]);


    useEffect(() => {
      if (!user) return;

      let warningTimer: any;
      let logoutTimer: any;
      let countdown: any;

      const setupTimers = async () => {
        try {
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();

          if (!accessToken) return;

          const decoded: any = jwtDecode(accessToken);
          const expiry = decoded.exp * 1000;

          const now = Date.now();

          const warningTime = expiry - (60 * 1000); // 1 min before
          const timeToWarning = warningTime - now;
          const timeToLogout = expiry - now;

          console.log("⏳ Token expires at:", new Date(expiry));

            if (timeToWarning > 0) {
              warningTimer = setTimeout(() => {
                console.log("⚠️ Showing session warning");

                // ✅ Directly show modal
                setShowSessionWarning(true);
                setSecondsLeft(60);
                clearInterval(countdown); // safety (optional)

                // ✅ Start countdown
                countdown = setInterval(() => {
                  setSecondsLeft(prev => {
                    if (prev <= 1) {
                      clearInterval(countdown);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);

              }, timeToWarning);
            }

          if (timeToLogout > 0) {
            logoutTimer = setTimeout(async () => {
              console.log("❌ Session expired → logout");
              await signOut();
              setUser(null);
              window.location.replace(LOGIN_ROUTE);
            }, timeToLogout);
          }

        } catch (err) {
          console.error("Session decode error", err);
        }
      };

      setupTimers();

      return () => {
        clearTimeout(warningTimer);
        clearTimeout(logoutTimer);
        clearInterval(countdown);
      };

    }, [user]);

  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === "amplify-signout") {
        setUser(null);
        window.location.replace(LOGIN_ROUTE);
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);


    useEffect(() => {
      if (!user) return;

      const loadAccess = async () => {
        try {
          await refreshUserData(); // role API
        } catch (e) {
          console.error("Access fetch failed", e);
        } finally {
          setIsAccessChecked(true);
        }
      };

      loadAccess();
    }, [user]);

  const extendSession = async () => {
    try {
      console.log("🔄 Refreshing session");

      await fetchAuthSession({ forceRefresh: true });

      setShowSessionWarning(false);
      setSecondsLeft(0); //✅ stop UI countdown

      getUser().catch(() => {});

      console.log("✅ Session extended");

    } catch (error) {
      console.error("❌ Session refresh failed", error);
      await signOut({ global: true });
      window.location.replace(LOGIN_ROUTE);
    }
  };

  const checkAuthState = async () => {
    try {
      await getCurrentUserCompact();
      getUser();
    } catch (error) {
        setUser(null);
      console.log("Not signed in");
      setAuthChecked(true);
    }
  };

  const getUser = async () => {
    try {
      const session = await fetchAuthSession();

      // ✅ If no valid session → user not logged in
      if (!session.tokens?.idToken) {
        setUser(null);
        return;
      }

      const payload = session.tokens.idToken.payload;

      // ✅ Build user WITHOUT calling Cognito APIs
      const user = {
        userId: payload.sub ?? "",
        username: payload.sub ?? "",
        signInDetails: {
          loginId: payload.email as string,
        },
      };

      setUser(user);

    } catch (error) {
      console.warn("Session fetch failed:", error);
      setUser(null);
    } finally {
      // ✅ CRITICAL: always allow UI to render
      setAuthChecked(true);
    }
  };


    const handleSignOut = async () => {
      try {
          setIsLoggingOut(true);
        setShowSessionWarning(false);
        setSecondsLeft(0);

        await signOut({ global: true });
        setUser(null);
        window.location.replace(LOGIN_ROUTE);
      } catch (error) {
        console.error("Error signing out: ", error);
        setIsLoggingOut(false);
      }
    };


  const handleSignIn = async (
    providerValue: string,
    username?: string,
    password?: string
  ) => {
    try {
      if (providerValue === "userguide") {
        console.log("Opening user guide");
        return;
      }

      if (providerValue === "cognito" && username && password) {
        await signIn({ username, password });
      } else {
        await signInWithRedirect({
          provider: {
            custom: providerValue,
          },
        });
      }
      await getUser();
      // Refresh user data in UserContext
      await refreshUserData();

      // ✅ mark access ready
      setIsAccessChecked(true);

    } catch (error) {
      console.error("Error signing in:", error);
      throw error;
    }
  };


    if (!authChecked || (user && !isAccessChecked)) {
      return <Loader />;
    }


  return (
    <ChakraProvider theme={theme}>
        <Modal isOpen={showSessionWarning} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false}>
          <ModalOverlay />
          <ModalContent border="2px solid red">
            <ModalHeader color="red.500">⚠️ Session Expiring</ModalHeader>
            <ModalBody>
              Your session will expire in <b>{secondsLeft}</b> seconds unless you continue.
            </ModalBody>
            <ModalFooter>
              <Button autoFocus colorScheme="blue" mr={3} onClick={extendSession}>
                Continue Session
              </Button>
              <Button variant="ghost" onClick={handleSignOut}>
                Logout
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {isLoggingOut && (
          <Flex
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            bg="rgba(0, 0, 0, 0.5)"
            zIndex="9999"
            align="center"
            justify="center"
            direction="column"
          >
            <Loader />  {/* your existing loader */}

            <Text mt={4} color="white" fontSize="lg" fontWeight="medium">
              Logging out...
            </Text>
          </Flex>
        )}
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <TableDataProvider>
              <Routes>
                {user ? (
                  <Route
                    path={HOME_ROUTE}
                    element={
                      <Suspense fallback={<Loader />}>
                        <ProtectedRoute
                          element={<AppLayout signOut={handleSignOut} />}
                        />
                      </Suspense>
                    }
                  >
                    <Route
                      index
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<HomePage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={DOWNLOAD_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<DownloadPage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={HELP_AND_SUPPORT_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<HelpAndSupportPage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={UPLOAD_PAGE_REDEFINED_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<UploadPage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={UPDATE_FILES_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<UpdateFilesPage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={PORTAL_CONTROL_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<PortalControlPage />} />
                        </Suspense>
                      }
                    />
                    <Route
                      path={DASHBOARD_ROUTE}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ProtectedRoute element={<NewSUDashboard />} />
                        </Suspense>
                      }
                    />

                    <Route
                      path="*"
                      element={<Navigate to={HOME_ROUTE} replace />}
                    />
                  </Route>
                ) : (
                  <>
                  <Route
                    path={LOGIN_ROUTE}
                    element={
                      <LoginComponent onLogin={handleSignIn} error={error} />
                    }
                  />
                    <Route
                      path={SIGNUP_ROUTE}
                      element={<SignUpPage />}
                    />
                <Route
                  path="*"
                      element={<Navigate to={LOGIN_ROUTE} replace />}
                />
                  </>
                )}
              </Routes>
            </TableDataProvider>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </ChakraProvider>
  );
};

export default App;
